type ShiftSummary = {
  shiftId: string;
  name: string;
  startTime: string;
  endTime: string;
  count: number;
};

type ShiftStatistics = {
  shifts: ShiftSummary[];
  totalEmployees: number;
};

// "17:00:00" -> "05:00 PM"
const formatShiftTime = (time: string) => {
  const [hours, minutes] = (time ?? "").split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "--";
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
};

const ClockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const EmployeeShiftSummary: React.FC<{
  statistics?: ShiftStatistics;
}> = ({statistics}) => {
  const shifts = statistics?.shifts ?? [];
  if (shifts.length === 0) return null;

  const total = statistics?.totalEmployees ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
      <div className="flex flex-col justify-between rounded-xl border border-[#1D4ED8]/20 bg-[#EFF6FF] p-4">
        <div className="flex items-start justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#1D4ED8]">
            All Shifts
          </span>
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[#1D4ED8]">
            <UsersIcon />
          </span>
        </div>
        <div className="mt-2 text-2xl font-bold text-[#0F172A]">{total}</div>
        <div className="mt-1 text-xs text-[#475569]">
          Employees transported across {shifts.length} shifts
        </div>
      </div>

      {shifts.map(shift => {
        const share = total > 0 ? Math.round((shift.count / total) * 100) : 0;
        const isEmpty = shift.count === 0;
        return (
          <div
            key={shift.shiftId}
            className="flex flex-col justify-between rounded-xl border border-[#E2E8F0] bg-white p-4 transition-colors duration-150 hover:border-[#CBD5E1]"
          >
            <div className="flex items-start justify-between gap-2">
              <span
                className="truncate text-xs font-semibold uppercase tracking-wide text-[#1D4ED8]"
                title={shift.name}
              >
                {shift.name}
              </span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F1F5F9] text-[#475569]">
                <ClockIcon />
              </span>
            </div>
            <div
              className={`mt-2 text-2xl font-bold ${isEmpty ? "text-[#94A3B8]" : "text-[#0F172A]"}`}
            >
              {shift.count}
            </div>
            <div className="mt-1 text-xs text-[#64748B]">
              {formatShiftTime(shift.startTime)} –{" "}
              {formatShiftTime(shift.endTime)}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div
                className="h-1.5 flex-1 overflow-hidden rounded-sm bg-[#F1F5F9]"
                role="progressbar"
                aria-label={`${shift.name} share of employees`}
                aria-valuenow={share}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full rounded-sm bg-[#1D4ED8]"
                  style={{width: `${share}%`}}
                />
              </div>
              <span className="w-9 text-right text-xs font-semibold text-[#475569]">
                {share}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
